import { Stepper } from "../components/Stepper";
import { Views, FormData } from "../types";
import {
  conditionsMap,
  operatorMessages,
  fieldTypeMessages,
  conditionsByAdverseActionLetterType
} from "../constants";
import { Checkbox } from "shared/components/forms/Checkbox";
import { TextArea } from "shared/components";
import {
  FieldName,
  RuleSetDecision
} from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/types";
import WarningFillIcon from "shared/icons/warningFillIcon.svg?react";
import { Edit03 } from "untitledui-js-base";
import { DecisionAction } from "shared/types/application";
import { Reasons } from "types/application";
import { Rule } from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/types";
import { Button, ButtonVariant } from "shared/components";
import { toMoney } from "shared/utils";
import { RecommendationMethod } from "types/landlord/verificationTemplate";
import {
  RuleSet,
  GroupType
} from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/types";
import { cx } from "shared/utils";
import { AdverseActionLetterType } from "api/landlord/templates/types";

export const ConditionsView = ({
  formData,
  updateFormData,
  view,
  flow,
  ruleReasons,
  recommendation,
  showDetailedConditionsView,
  setShowDetailedConditionsView,
  recommendationMethod,
  ruleCriteria,
  adverseActionTemplate
}: {
  formData: FormData;
  updateFormData: (updates: Partial<FormData>) => void;
  view: Views;
  flow: Views[];
  ruleReasons: Reasons[] | undefined;
  recommendation?: DecisionAction;
  showDetailedConditionsView: boolean;
  setShowDetailedConditionsView: React.Dispatch<React.SetStateAction<boolean>>;
  recommendationMethod?: RecommendationMethod;
  ruleCriteria?: RuleSet[];
  adverseActionTemplate?: AdverseActionLetterType;
}) => {
  const getMetConditionsRules = (ruleSetRecommendation: RuleSetDecision) => {
    const rules = ruleReasons?.filter(
      (rs) => rs.rule_set_type === ruleSetRecommendation
    );

    const fieldNames = rules?.flatMap((ruleSet) =>
      ruleSet.rules
        .filter(
          (rule) =>
            rule.result === (ruleSetRecommendation !== RuleSetDecision.Approve)
        )
        .map((rule) => rule.field_name)
    );
    return Array.from(new Set(fieldNames || []));
  };

  const mapRuleToText = (
    fieldName,
    rules,
    ruleSetRecommendation,
    operatorMessages,
    fieldTypeMessages
  ) => {
    const rule = rules?.find((r) => r.field_name === fieldName);

    if (!rule) return "";

    const { operator, threshold, field_type, rule_id } = rule;
    const operatorText = operatorMessages[operator] || operator;

    const groupType = ruleCriteria?.reduce<GroupType | undefined>(
      (ret, val) => {
        if (ret) return ret;
        if (val.type === ruleSetRecommendation) {
          const match = val.rules.find((r) => r.id === rule_id);
          if (match) return match.group_type;
        }
        return ret;
      },
      undefined
    );

    const fieldTypeText =
      (field_type && fieldTypeMessages[field_type]) ||
      field_type ||
      (groupType && `the ${groupType.replaceAll("_", " ")} of`);

    const fieldText =
      fieldName === FieldName.OfacRecords
        ? "sanctions (OFAC) convictions"
        : fieldName.replace(/_/g, " ");

    const formatThreshold = (value) => {
      const fieldsRequiringMoneyFormat = [
        "Gross income",
        "Net income",
        "Balance past due",
        "Balance of collections"
      ];
      return fieldsRequiringMoneyFormat.includes(fieldText)
        ? toMoney(value)
        : value;
    };

    const formattedThreshold = formatThreshold(threshold);

    if (ruleSetRecommendation === RuleSetDecision.Approve) {
      if (field_type?.startsWith("within_"))
        return `At least one applicant doesn't have ${fieldText} ${operatorText} ${formattedThreshold} ${fieldTypeText}`;
      if (fieldTypeText === "the group average of")
        return `${fieldTypeText} ${fieldText} isn't ${operatorText} ${formattedThreshold}`;
      return `${fieldTypeText} ${fieldText} isn't ${operatorText} ${formattedThreshold}`;
    } else {
      if (field_type?.startsWith("within_"))
        return `At least one applicant had ${fieldText} ${operatorText} ${formattedThreshold} ${fieldTypeText}`;
      if (fieldTypeText === "the group average of")
        return `${fieldTypeText} ${fieldText} was ${operatorText} ${formattedThreshold}`;
      return `${fieldTypeText} ${fieldText} ${operatorText} ${formattedThreshold}`;
    }
  };

  const getReasonMessage = (
    ruleSetRecommendation: RuleSetDecision
  ): string[] | undefined => {
    const metConditionsRules = getMetConditionsRules(ruleSetRecommendation);

    const rules = ruleReasons?.reduce<Rule[]>((acc, rs) => {
      if (rs.rule_set_type === ruleSetRecommendation)
        return acc.concat(rs.rules);
      return acc;
    }, []);

    return metConditionsRules.map((fieldName) =>
      mapRuleToText(
        fieldName,
        rules,
        ruleSetRecommendation,
        operatorMessages,
        fieldTypeMessages
      )
    );
  };

  if (
    showDetailedConditionsView &&
    recommendation &&
    recommendation !== DecisionAction.Approve &&
    formData.decision === recommendation &&
    recommendationMethod == "rule_based" &&
    adverseActionTemplate === AdverseActionLetterType.Generic
  )
    return (
      <div className="h-full">
        <Stepper view={view} flow={flow} />
        <div className="p-6">
          <div className="space-y-4 pb-6">
            {recommendation === DecisionAction.Decline
              ? [
                  ...new Set(
                    getMetConditionsRules(RuleSetDecision.Decline).map(
                      (rule) => conditionsMap[rule]
                    )
                  )
                ]
                  .filter(Boolean)
                  .map((condition, index) => (
                    <p
                      key={index}
                      className="flex items-center text-base text-primary-500 bg-primary-20 p-3 rounded-lg font-medium leading-6 text-center"
                    >
                      <WarningFillIcon className="" />
                      <span className="ml-2">{condition}</span>
                    </p>
                  ))
              : [
                  ...new Set(
                    getMetConditionsRules(RuleSetDecision.Approve).map(
                      (rule) => conditionsMap[rule]
                    )
                  )
                ]
                  .filter(Boolean)
                  .map((condition, index) => (
                    <p
                      key={index}
                      className="flex items-center text-base text-primary-500 bg-primary-20 p-3 rounded-lg font-medium leading-6 text-center"
                    >
                      <WarningFillIcon className="" />
                      <span className="ml-2">{condition}</span>
                    </p>
                  ))}
            <div className="mb-6 border-b border-alpha-40 pb-6">
              <Button
                Icon={Edit03}
                className="mt-6 mb-4 text-base font-bold leading-6 text-primary-500 flex items-center"
                variant={ButtonVariant.TEXT}
                onClick={() => setShowDetailedConditionsView(false)}
              >
                <span className="ml-2">Add/Edit reasons</span>
              </Button>
            </div>
            <div className="mt-6">
              <p className="text-base font-bold mt-6">
                Reason(s) were recommended based on failed criteria:
              </p>
              <ul className="list-disc list-inside text-base font-medium text-content-400 leading-6 mt-3">
                {recommendation === DecisionAction.Decline
                  ? getReasonMessage(RuleSetDecision.Decline)?.map(
                      (message, index) => (
                        <li key={index} className="first-letter:uppercase">
                          {message}
                        </li>
                      )
                    )
                  : getReasonMessage(RuleSetDecision.Approve)?.map(
                      (message, index) => (
                        <li key={index} className="first-letter:uppercase">
                          {message}
                        </li>
                      )
                    )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );

  const applicationConditions =
    conditionsByAdverseActionLetterType[
      adverseActionTemplate || AdverseActionLetterType.Generic
    ];

  return (
    <div className="flex flex-col h-full">
      <Stepper view={view} flow={flow} />
      <div className="p-6">
        <div
          className={cx(
            "grid gap-2",
            adverseActionTemplate === AdverseActionLetterType.Washington
              ? "grid-cols-1"
              : "grid-cols-2"
          )}
        >
          {applicationConditions.map((condition) => (
            <div key={condition.id}>
              <Checkbox
                id={condition.id}
                checked={formData.conditions.includes(String(condition.id))}
                onChange={() =>
                  updateFormData({
                    conditions: formData.conditions.includes(condition.id)
                      ? formData.conditions.filter((id) => id !== condition.id)
                      : [...formData.conditions, condition.id]
                  })
                }
                label={condition.text}
                labelClassName="!text-base"
              />
            </div>
          ))}
        </div>

        <div className="mt-6">
          <TextArea
            className="max-h-[128px] h-full"
            id="additionalCondition"
            rows={15}
            label={
              <>
                Specify other reason(s) below:{" "}
                <span className="text-content-100">(optional)</span>
              </>
            }
            value={formData.additionalCondition}
            onChange={(e) =>
              updateFormData({ additionalCondition: e.target.value })
            }
            placeholder="Enter reason here"
          />
        </div>
      </div>
    </div>
  );
};
